import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import ReactGA from 'react-ga4'; 

// Importación de Proveedores de Estado Global
import { CartProvider } from './context/CartContext'; 
import { AuthProvider } from './context/AuthContext'; 

// Importación del Guardián de Rutas y Layouts (Estos se cargan normal porque siempre están visibles o son reglas base)
import ProtectedRoute from './components/ProtectedRoute'; 
import AdminLayout from './layouts/AdminLayout';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import CartDrawer from './components/CartDrawer'; 
import ChatBot from './components/ChatBot'; 

// =========================================================
// 🚀 OPTIMIZACIÓN EXTREMA: CODE SPLITTING (React.lazy)
// Esto divide tu JavaScript en pedazos pequeños para que la página cargue en milisegundos
// =========================================================

// Vistas Públicas
const Home = lazy(() => import('./pages/shop/Home')); 
const Catalogo = lazy(() => import('./pages/shop/Catalogo'));
const Producto = lazy(() => import('./pages/shop/Productopage'));
const Perfil = lazy(() => import('./pages/shop/Perfil')); 
const Checkout = lazy(() => import('./pages/shop/Checkout')); 

// Vistas de Autenticación
const Register = lazy(() => import('./pages/auth/Register'));
const Login = lazy(() => import('./pages/auth/Login'));
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/auth/ResetPassword'));

// Vistas Administrativas
const Dashboard = lazy(() => import('./pages/admin/Dashboard'));
const Inventario = lazy(() => import('./pages/admin/Inventario'));
const AddProduct = lazy(() => import('./pages/admin/AddProduct'));
const Pedidos = lazy(() => import('./pages/admin/Pedidos'));
const Cupones = lazy(() => import('./pages/admin/Cupones'));
const Estadisticas = lazy(() => import('./pages/admin/Estadisticas'));
const Configuracion = lazy(() => import('./pages/admin/Configuracion')); 

// =========================================================
// 🚨 1. INICIALIZAR GOOGLE ANALYTICS 4
// =========================================================
const TRACKING_ID = "G-MFVKHEHY47"; // Tu ID de Medición real
ReactGA.initialize(TRACKING_ID);

// =========================================================
// 🚨 2. RASTREADOR DE VISTAS DE PÁGINA (PAGEVIEWS)
// =========================================================
const PageViewTracker = () => {
  const location = useLocation();
  
  useEffect(() => {
    // Le avisa a Google Analytics cada vez que cambia la URL
    ReactGA.send({ 
      hitType: "pageview", 
      page: location.pathname + location.search,
      title: document.title
    });
  }, [location]);
  
  return null; 
};

// Este Layout envolverá a las páginas de la tienda
const ShopLayout = () => {
  return (
    <>
      <Header />
      <CartDrawer /> 
      <Outlet /> 
      <Footer />
      <ChatBot /> 
    </>
  );
};

// Componente de carga visual mientras se descargan las rutas
const LoaderNeoClic = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#030712', color: '#ff4d00', fontSize: '1.5rem', fontWeight: 'bold' }}>
    CARGANDO NEOCLIC...
  </div>
);

function App() {
  return (
    <CartProvider> 
      <AuthProvider>
        <BrowserRouter>
          <PageViewTracker />
          
          {/* 🚀 Envolvemos TODAS las rutas en Suspense para manejar la carga asíncrona */}
          <Suspense fallback={<LoaderNeoClic />}>
            <Routes>
              
              {/* ==========================================
                  RUTAS DE LA TIENDA
                  ========================================== */}
              <Route element={<ShopLayout />}>
                <Route path="/" element={<Home />} />
                <Route path="/catalogo" element={<Catalogo />} />
                <Route path="/producto/:id" element={<Producto />} />
                <Route path="/perfil" element={<Perfil />} />
                <Route path="/checkout" element={<Checkout />} />
              </Route>

              {/* ==========================================
                  RUTAS DE AUTENTICACIÓN
                  ========================================== */}
              <Route path="/register" element={<Register />} />
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password/:id/:token" element={<ResetPassword />} />
              
              {/* ==========================================
                  RUTAS DE ADMINISTRADOR
                  ========================================== */}
              <Route 
                path="/admin" 
                element={
                  <ProtectedRoute>
                    <AdminLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="inventario" element={<Inventario />} />
                <Route path="nuevo-producto" element={<AddProduct />} />
                <Route path="pedidos" element={<Pedidos />} />
                <Route path="cupones" element={<Cupones />} />
                <Route path="ventas" element={<Estadisticas />} /> 
                <Route path="estadisticas-ventas" element={<Estadisticas />} /> 
                <Route path="configuracion" element={<Configuracion />} />
              </Route>

            </Routes>
          </Suspense>
        </BrowserRouter>
      </AuthProvider>
    </CartProvider>
  );
}

export default App;